import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LocationResolver from "@/components/LocationResolver";
import { FEATURE_GROUPS, LEGACY_FEATURES, selectedFeature, featureLabel } from "@/components/PropertyFeatures";
import { useTranslation } from 'react-i18next'
import { supportedLanguages } from '@/i18n';

const T = (props) => <Input {...props} className="mt-1 rounded-sm focus-visible:ring-1 focus-visible:ring-[#C5A059]" />;

export default function PropertyForm({ open, onOpenChange, meta, initial, onSubmit }) {
  const { t, i18n } = useTranslation()
  const featureLang = i18n.language;
  const [form, setForm] = useState(
    initial || {
      title: "", status: "Available", category: "Apartment", transaction_type: "Sale",
      visibility: "public", country: "Portugal", district: "", municipality: "", city: "",
      address: "", postal_code: "", lat: "", lng: "", asking_price: "", min_price: "",
      market_value: "", commission_pct: 5, currency: "EUR", estimated_roi: "",
      gross_area: "", land_area: "", private_area: "", bedrooms: "", bathrooms: "",
      floors: "", year_built: "", energy_certificate: "", condition: "", description: "",
      development_potential: "", possible_units: "", investment_strengths: "",
      investment_risks: "", exit_strategy: "", cover_image: "",
      garden: false, pool: false, elevator: false, terrace: false,
      student_residence_potential: false, senior_residence_potential: false, hotel_potential: false,
    }
  );

  const [contentLanguage, setContentLanguage] = useState("en");
  const [priceUponRequest, setPriceUponRequest] = useState(!initial?.asking_price);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const localizedValue = (field) => {
    if (contentLanguage === "en") return form[field] || "";
    return form.translations?.[contentLanguage]?.[field] || "";
  };

  const setLocalizedValue = (field, value) => {
    if (contentLanguage === "en") {
      set(field, value);
      return;
    }
    setForm((current) => ({
      ...current,
      translations: {
        ...(current.translations || {}),
        [contentLanguage]: {
          ...(current.translations?.[contentLanguage] || {}),
          [field]: value,
        },
      },
    }));
  };
  const numFields = ["asking_price", "min_price", "market_value", "commission_pct", "estimated_roi",
    "gross_area", "land_area", "private_area", "bedrooms", "bathrooms", "floors", "year_built",
    "possible_units", "lat", "lng"];

  const submit = () => {
    const payload = { ...form };
    numFields.forEach((k) => {
      if (payload[k] === "" || payload[k] == null) {
        if (initial) payload[k] = null;
        else delete payload[k];
      }
      else payload[k] = Number(payload[k]);
    });
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial ? t('editProperty', 'Edit Property') : t('addProperty', 'Add Property')}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic">
          <TabsList className="rounded-sm bg-slate-100 flex-wrap h-auto">
            <TabsTrigger value="basic" className="rounded-sm">{t('identification', 'Identification')}</TabsTrigger>
            <TabsTrigger value="location" className="rounded-sm">{t('location', 'Location')}</TabsTrigger>
            <TabsTrigger value="financial" className="rounded-sm">{t('financial', 'Financial')}</TabsTrigger>
            <TabsTrigger value="chars" className="rounded-sm">{t('characteristics', 'Characteristics')}</TabsTrigger>
            <TabsTrigger value="invest" className="rounded-sm">{t('investment', 'Investment')}</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="rounded-sm border border-[#C5A059]/40 bg-[#C5A059]/5 p-3">
              <Label>{t('contentLanguage', 'Property content language')}</Label>
              <select
                value={contentLanguage}
                onChange={(e) => setContentLanguage(e.target.value)}
                className="mt-1 h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
              >
                {supportedLanguages.map((language) => (
                  <option key={language.code} value={language.code}>{language.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                {t('contentLanguageHelp', 'Enter the title, description and investment text for each language. English is used as the fallback.')}
              </p>
            </div>
            <div>
              <Label>{t('title', 'Title *')}</Label>
              <T data-testid="prop-title" value={localizedValue("title")} onChange={(e) => setLocalizedValue("title", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('status', 'Status')}</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="prop-status" className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {meta?.property_statuses?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('category', 'Category')}</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="prop-category" className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {meta?.property_categories?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('transactionType', 'Transaction Type')}</Label>
                <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {meta?.transaction_types?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('visibility', 'Visibility')}</Label>
                <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="public">{t('public', 'Public')}</SelectItem>
                    <SelectItem value="confidential">{t('confidential', 'Confidential')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('coverImageUrl', 'Cover Image URL')}</Label>
              <T value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <Label>{t('description', 'Description')}</Label>
              <Textarea value={localizedValue("description")} onChange={(e) => setLocalizedValue("description", e.target.value)} className="mt-1 rounded-sm" rows={5} />
            </div>
          </TabsContent>

          <TabsContent value="location" className="space-y-4 pt-4">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <LocationResolver
                initial={form.lat && form.lng ? { lat: Number(form.lat), lng: Number(form.lng), city: form.city, region: form.district, country: form.country } : null}
                onConfirm={(pt) => {
                  set("lat", pt.lat); set("lng", pt.lng);
                  if (pt.country) set("country", pt.country);
                  if (pt.region) set("district", pt.region);
                  if (pt.city) set("city", pt.city);
                  if (pt.address) set("address", pt.address);
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('country', 'Country')}</Label><T value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
              <div><Label>{t('districtRegion', 'District / Region')}</Label><T value={form.district} onChange={(e) => set("district", e.target.value)} /></div>
              <div><Label>{t('municipality', 'Municipality')}</Label><T value={form.municipality} onChange={(e) => set("municipality", e.target.value)} /></div>
              <div><Label>{t('city', 'City')}</Label><T value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
              <div><Label>{t('streetAddress', 'Street Address')}</Label><T value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
              <div><Label>{t('postalCode', 'Postal Code')}</Label><T value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></div>
              <div><Label>{t('latitude', 'Latitude')}</Label><T type="number" value={form.lat} onChange={(e) => set("lat", e.target.value)} /></div>
              <div><Label>{t('longitude', 'Longitude')}</Label><T type="number" value={form.lng} onChange={(e) => set("lng", e.target.value)} /></div>
            </div>
          </TabsContent>

          <TabsContent value="financial" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('askingPrice3', 'Asking Price (€)')}</Label>
                <div className="space-y-2">
                  <T
                    data-testid="prop-price"
                    type="number"
                    value={form.asking_price}
                    disabled={priceUponRequest}
                    onChange={(e) => set("asking_price", e.target.value)}
                  />
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={priceUponRequest}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setPriceUponRequest(checked);
                        if (checked) set("asking_price", "");
                      }}
                    />
                    <span>{t('priceUponRequest', 'Price upon request')}</span>
                  </label>
                </div>
              </div>
              <div><Label>{t('minimumPrice2', 'Minimum Price (€)')}</Label><T type="number" value={form.min_price} onChange={(e) => set("min_price", e.target.value)} /></div>
              <div><Label>{t('marketValue2', 'Market Value (€)')}</Label><T type="number" value={form.market_value} onChange={(e) => set("market_value", e.target.value)} /></div>
              <div><Label>{t('commission', 'Commission %')}</Label><T type="number" value={form.commission_pct} onChange={(e) => set("commission_pct", e.target.value)} /></div>
              <div><Label>{t('estimatedRoi2', 'Estimated ROI %')}</Label><T type="number" value={form.estimated_roi} onChange={(e) => set("estimated_roi", e.target.value)} /></div>
              <div><Label>{t('currency', 'Currency')}</Label><T value={form.currency} onChange={(e) => set("currency", e.target.value)} /></div>
            </div>
          </TabsContent>

          <TabsContent value="chars" className="space-y-4 pt-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>{t('grossAreaM', 'Gross Area (m²)')}</Label><T type="number" value={form.gross_area} onChange={(e) => set("gross_area", e.target.value)} /></div>
              <div><Label>{t('landAreaM', 'Land Area (m²)')}</Label><T type="number" value={form.land_area} onChange={(e) => set("land_area", e.target.value)} /></div>
              <div><Label>{t('privateAreaM', 'Private Area (m²)')}</Label><T type="number" value={form.private_area} onChange={(e) => set("private_area", e.target.value)} /></div>
              <div><Label>{t('bedrooms', 'Bedrooms')}</Label><T type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></div>
              <div><Label>{t('bathrooms', 'Bathrooms')}</Label><T type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} /></div>
              <div><Label>{t('floors', 'Floors')}</Label><T type="number" value={form.floors} onChange={(e) => set("floors", e.target.value)} /></div>
              <div><Label>{t('yearBuilt2', 'Year Built')}</Label><T type="number" value={form.year_built} onChange={(e) => set("year_built", e.target.value)} /></div>
              <div><Label>{t('energyCertificate', 'Energy Certificate')}</Label><T value={form.energy_certificate} onChange={(e) => set("energy_certificate", e.target.value)} /></div>
              <div><Label>{t('condition', 'Condition')}</Label><T value={form.condition} onChange={(e) => set("condition", e.target.value)} /></div>
            </div>
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h3 className="font-serif text-lg">{featureLabel("heading", featureLang)}</h3>
              {FEATURE_GROUPS.map((group) => <fieldset key={group.id} className="space-y-3">
                <legend className="font-medium text-sm text-[#0B2545]">{featureLabel(group.id, featureLang)}</legend>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {group.items.map((id) => <label key={id} className="flex items-center gap-2 text-sm">
                    <Switch checked={!!selectedFeature(form, id)} onCheckedChange={(checked) => setForm((current) => ({
                      ...current,
                      amenities: { ...(current.amenities || {}), [id]: checked },
                      ...(LEGACY_FEATURES.includes(id) ? { [id]: checked } : {}),
                    }))} />{featureLabel(id, featureLang)}
                  </label>)}
                </div>
              </fieldset>)}
            </div>
          </TabsContent>

          <TabsContent value="invest" className="space-y-4 pt-4">
            <div><Label>{t('developmentPotential2', 'Development Potential')}</Label><Textarea value={localizedValue("development_potential")} onChange={(e) => setLocalizedValue("development_potential", e.target.value)} className="mt-1 rounded-sm" rows={4} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('possibleUnitsRooms', 'Possible Units / Rooms')}</Label><T type="number" value={form.possible_units} onChange={(e) => set("possible_units", e.target.value)} /></div>
              <div><Label>{t('exitStrategy2', 'Exit Strategy')}</Label><Textarea value={localizedValue("exit_strategy")} onChange={(e) => setLocalizedValue("exit_strategy", e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
            </div>
            <div><Label>{t('investmentStrengths2', 'Investment Strengths')}</Label><Textarea value={localizedValue("investment_strengths")} onChange={(e) => setLocalizedValue("investment_strengths", e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
            <div><Label>{t('investmentRisks2', 'Investment Risks')}</Label><Textarea value={localizedValue("investment_risks")} onChange={(e) => setLocalizedValue("investment_risks", e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {[["student_residence_potential", "Student residence"], ["senior_residence_potential", "Senior residence"], ["hotel_potential", t('hotelTourism', 'Hotel / tourism')]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} /> {l}
                </label>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>{t('cancel', 'Cancel')}</Button>
          <Button
            data-testid="prop-save"
            disabled={!form.title}
            onClick={submit}
            className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white"
          >
            {initial ? t('saveChanges', 'Save changes') : t('createProperty', 'Create property')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
